import { useState } from 'react';
import { PromptSelect } from './PromptSelect';

interface CollapsiblePromptProps {
  title: string;
  value?: string;
  onChange?: (value: string) => void;
  defaultExpanded?: boolean;
  // For context prompt dropdown
  selectOptions?: { id: string; label: string }[];
  selectedOption?: string;
  onSelectOption?: (id: string) => void;
  // Display mode
  selectOnly?: boolean;
}

export function CollapsiblePrompt({
  title,
  value,
  onChange,
  defaultExpanded = false,
  selectOptions,
  selectedOption,
  onSelectOption,
  selectOnly = false,
}: CollapsiblePromptProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  // If selectOnly mode, render simplified version
  if (selectOnly) {
    return (
      <>
          {selectOptions && onSelectOption && (
            <PromptSelect
              options={selectOptions}
              selectedOption={selectedOption}
              onSelectOption={onSelectOption}
            />
          )}
      </>
    );
  }

  return (
    <div className="border border-gray-300 rounded-md overflow-hidden">
      {/* Header */}
      <button
        type="button"
        className="flex items-center justify-between px-3 py-2 bg-gray-50 cursor-pointer hover:bg-gray-100 transition w-full text-left"
        onClick={() => setIsExpanded(!isExpanded)}
        aria-expanded={isExpanded}
        aria-controls={`collapsible-content-${title.replace(/\s+/g, '-').toLowerCase()}`}
      >
        <div className="flex items-center gap-2">
          {/* Chevron */}
          <svg
            className={`w-4 h-4 text-gray-500 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5l7 7-7 7"
            />
          </svg>
          <span className="sr-only">{isExpanded ? 'Collapse' : 'Expand'}</span>
          <span className="text-sm font-medium text-gray-700">{title}</span>
        </div>

        {/* Dropdown (if provided) */}
        {selectOptions && onSelectOption && (
          <PromptSelect
            options={selectOptions}
            selectedOption={selectedOption}
            onSelectOption={onSelectOption}
          />
        )}
      </button>

      {/* Content */}
      {isExpanded && value !== undefined && onChange && (
        <div
          className="border-t border-gray-300"
          id={`collapsible-content-${title.replace(/\s+/g, '-').toLowerCase()}`}
          role="region"
          aria-label={`${title} content`}
        >
          <textarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="w-full px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-inset resize-y"
            rows={10}
            placeholder="Enter prompt text..."
            aria-label={`${title} text input`}
          />
        </div>
      )}
    </div>
  );
}
